/* THE TYPESCRIPT TWIN OF THE COACH PROGRESSION READER — pure, no I/O, nothing wired.
 *
 * WHY A SECOND IMPLEMENTATION EXISTS AT ALL, WHEN "two statements of one rule" IS THIS
 * REPOSITORY'S DEFINING FAILURE (twenty-four counted instances, `docs/TRAP-CODEX.md`).
 * The plan of record (`docs/NEXT-WORK.md` W3, re-cut 2026-09-18) moves the weekly write to the
 * scheduled endpoint of 2026-09-10. That endpoint is Deno. **Deno cannot run Swift**, so the
 * choice was never "one implementation or two" — it was between a Swift host outside the phone
 * (infrastructure and credentials, which are his) and a twin PINNED BY CONSTRUCTION.
 *
 * ⚠️ THE PIN IS `tools/engine-tests/NextWeekFixture.swift`. The Swift emits every verdict it
 * reaches over a fixed corpus as one JSON document; this file's tests replay that document. The
 * Swift is the truth. **Nothing here may be "improved" — a divergence that makes this file nicer
 * is a divergence.** Where the two languages genuinely cannot agree, it is NAMED in a comment
 * beside the code rather than smoothed over, because a silent numeric difference between two
 * implementations of a load calculation is the worst failure this design can have.
 *
 * ⚠️ AND ONE THING IS MISSING, SAID HERE RATHER THAN DISCOVERED BY A READER WHO TRUSTED THE
 * FILE NAME. `CoachProgression.plan` delegates the actual §5 rules to `V6Progression.nextWeek`
 * — 2,123 lines of Swift. **That is not transcribed.** What IS transcribed is everything around
 * it: the grouping, the pairing, the derived state, the redistribution and the auto-apply
 * calendar — measured at 1,089 + 73 lines of Swift, and the layer where this system's real bugs
 * have actually lived (the split prescription that produced 23 units from 23 rows; the warm-up
 * squat whose history the working squat was reading; the non-uniform hold silently reduced).
 * 🔴 AS OF 2026-09-20 THE RULES **ARE** TRANSCRIBED — `_shared/v6rules.ts` is a complete twin of
 * `V6Progression`, and `_shared/coachPlan.ts` is the JOIN: its `plan()` mirrors
 * `CoachProgression.plan` and calls `v6rules.nextWeek` directly, building its input in place.
 * THIS FILE STILL KNOWS NOTHING ABOUT §5, deliberately, which is why the join is a third module
 * rather than a function here. The `RuleEngine` interface that used to stand in for the rules is
 * DELETED; the block where it stood says why a stale contract is worse than no contract.
 *
 * Every exported function names the Swift it mirrors, with a line number. Line numbers rot; the
 * FUNCTION name does not, so both are given and the name is what to search for.
 */

/* ── Numeric agreement, which is the first place two languages diverge ──────────────────────
 *
 * ⚠️ `Double.rounded()` IN SWIFT IS ROUND-HALF-AWAY-FROM-ZERO. `Math.round` IN JAVASCRIPT IS
 * ROUND-HALF-UP (toward +∞). They disagree on exactly the negative halves: Swift `(-0.5)
 * .rounded()` is `-1`, `Math.round(-0.5)` is `-0`. Today's loads are positive so this never
 * fires — which is precisely why it must be written down rather than left to `Math.round`: the
 * day a rule hands back a negative delta the two implementations would disagree by 0.25 kg on
 * one athlete's row, with nothing red anywhere.
 * Mirrors: Swift's `FloatingPoint.rounded()` as used by `AssistLoad.snapped`, AssistLoad.swift:68.
 */
export function roundHalfAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/** Mirrors `AssistLoad.smallestLoadStepKg`, AssistLoad.swift:62. */
export const SMALLEST_LOAD_STEP_KG = 0.25;

/** Mirrors `AssistLoad.snapped(_:)`, AssistLoad.swift:66. Nil in, nil out — a row with no load
 *  must not acquire one. */
export function snapped(value: number | null): number | null {
  if (value === null) return null;
  return roundHalfAwayFromZero(value / SMALLEST_LOAD_STEP_KG) * SMALLEST_LOAD_STEP_KG;
}

/* ⚠️ `String(format: "%.2f"/"%.3f")` IS USED AS AN EQUALITY KEY IN THREE PLACES IN THE SWIFT
 * (`SetRow.doseSignature`, `uniform`, `doseMoved`), so the twin must format IDENTICALLY or two
 * equal weights hash to two different signatures and a unit silently splits in half.
 * `toFixed` and `%.f` agree on every value reachable here — loads live on a 0.25 kg grid and
 * are exactly representable — and they are NOT guaranteed to agree on a value that is not,
 * because C rounds the binary value half-to-even and `toFixed` rounds the decimal expansion.
 * The fixture is what proves it for the corpus; this comment is what tells the next person that
 * a new unrepresentable value is the thing to suspect. */
function fixed(value: number, places: number): string {
  return value.toFixed(places);
}

// ── The types, mirroring CoachProgression.swift:55-208 ────────────────────────────────────────

/** Mirrors `CoachProgression.SetRow`, CoachProgression.swift:55.
 *  ⚠️ `position` is ZERO-BASED (`coach_workout_sets.position`). */
export interface SetRow {
  position: number;
  reps: number | null;
  weightKg: number | null;
  timeSeconds: number | null;
  restSeconds: number | null;
  /** `regular` | `warm_up` | `drop_set` | `failure` — migration 20260807000000. */
  setType: string;
}

export function setRow(p: Partial<SetRow> & { position: number }): SetRow {
  return {
    position: p.position,
    reps: p.reps ?? null,
    weightKg: p.weightKg ?? null,
    timeSeconds: p.timeSeconds ?? null,
    restSeconds: p.restSeconds ?? null,
    setType: p.setType ?? "regular",
  };
}

/** Mirrors `SetRow.doseSignature`, CoachProgression.swift:79.
 *  ⚠️ REST IS DELIBERATELY ABSENT — a superset's members legitimately carry different rest
 *  (v3 §2.5 puts "rest after this element" on the set), and the two halves of one split
 *  prescription differ in exactly that field. */
export function setDoseSignature(s: SetRow): string {
  return [
    s.reps === null ? "-" : String(s.reps),
    s.weightKg === null ? "-" : fixed(s.weightKg, 2),
    s.timeSeconds === null ? "-" : String(s.timeSeconds),
    s.setType,
  ].join("/");
}

/** Mirrors `CoachProgression.Row`, CoachProgression.swift:87.
 *  ⚠️ `supersetWithNext` is a BOUNDARY flag, not a group membership: it says "joined to the row
 *  after me", so nothing that reorders or inserts rows may carry it across unexamined. */
export interface Row {
  id: string;
  libraryExerciseId: string;
  name: string;
  sectionRole: SectionRole;
  /** Position within the whole day. THE PAIRING KEY, and it is an ORDER rather than a value. */
  ordinal: number;
  sets: SetRow[];
  supersetWithNext: boolean;
  isHold: boolean;
  /** `coach_exercises.progression_config` — the AUTHORED rule, with no state in it. */
  rule: unknown | null;
  /** `coach_exercises.is_quality_limited`. Mirrors `Row.isQualityLimited`,
   *  CoachProgression.swift:106.
   *  🔴 OPTIONAL, AND ABSENT MEANS **UNCLASSIFIED** — never `false`. The Swift tests `== true`
   *  and its own comment says why: "guessing from a name is what put a tuck lever on a bar".
   *  Only an explicit `true` ever flags. */
  isQualityLimited?: boolean | null;
  /** `coach_exercises.movement_pattern`. Mirrors `Row.movementPattern`,
   *  CoachProgression.swift:109. Absent means the row states no pattern, and a row with no
   *  pattern can neither be flagged nor be the thing something is flagged behind. */
  movementPattern?: string | null;
  /** W136 B2 S3 step 3b — the row's sets carry `rounds` or `distance_m` (columns `SetRow` does not model). Mirrors
   *  `Row.measuredInDistanceOrRounds`: FROZEN WHOLE by `plan`, and NEVER a hold — the Swift init stores
   *  `isHold && !measuredInDistanceOrRounds`, which `units` applies here (a run's time read as a hold fails a faster run). */
  measuredInDistanceOrRounds?: boolean;
  /** F15 4.1b — mirrors `Row.facts`: the row's own fields a rule tab may write (`coach_workout_exercises.tempo` / `notes` /
   *  `each_side` / `hidden`). Absent = null / false. A HIDDEN row is never shown to or logged by the client, so `pair`
   *  leaves it out of the day. */
  tempo?: string | null;
  note?: string | null;
  eachSide?: boolean;
  hidden?: boolean;
}

/** Mirrors `SectionRole`, Models/Program/SessionSection.swift:9. */
export type SectionRole = "warm_up" | "main" | "cool_down" | "recovery";

/** Mirrors `CoachProgression.LoggedSet`, CoachProgression.swift:132. */
export interface LoggedSet {
  reps: number | null;
  holdSeconds: number | null;
  weightKg: number | null;
  rpe: number | null;
  status: string;
  bandLabel: string | null;
}

export const isWarmupLog = (s: LoggedSet): boolean => s.status === "warmup";
export const isCompletedLog = (s: LoggedSet): boolean =>
  s.status === "completed" || s.status === "partial";

/** Mirrors `CoachProgression.LoggedRow`, CoachProgression.swift:153.
 *  ⚠️ `position` is ONE-BASED (`workout_session_exercises.position`), against `Row.ordinal`'s
 *  zero-based coach coordinates. **Never compared — only ORDERED.** See `pair`. */
export interface LoggedRow {
  position: number;
  libraryExerciseId: string | null;
  sets: LoggedSet[];
}

/** Mirrors `CoachProgression.LoggedSession`, CoachProgression.swift:162. */
export interface LoggedSession {
  /** An ISO timestamp. ⚠️ A `Date` is not carried: this file is pure and a Date would make two
   *  implementations depend on two timezone databases. The caller owns the parsing. */
  date: string;
  performanceNotes: string | null;
  rows: LoggedRow[];
}

/** Mirrors `CoachProgression.reconstructionStamp`, CoachProgression.swift:187. */
export const RECONSTRUCTION_STAMP = "Reconstructed ";

/** Mirrors `LoggedSession.isReconstructed`, CoachProgression.swift:184.
 *  ⚠️ A PREFIX, NOT A SEARCH, and the reopen condition is exact: matching anywhere in the string
 *  would let an athlete who types the word disable a week of their own progression. */
export function isReconstructed(session: LoggedSession): boolean {
  return (session.performanceNotes ?? "").startsWith(RECONSTRUCTION_STAMP);
}

/** Mirrors `CoachProgression.Unit`, CoachProgression.swift:246. */
export interface Unit {
  libraryExerciseId: string;
  name: string;
  sectionRole: SectionRole;
  isHold: boolean;
  rule: unknown | null;
  /** The rows this unit is stored across, in day order. Two for a split prescription. */
  rowIds: string[];
  sets: SetRow[];
  ordinal: number;
  /** W136 B2 S3 step 3b — a row of this unit is measured in rounds or distance: FROZEN WHOLE. Mirrors `Unit.frozen`; set only
   *  when true, so a unit built before the flag existed deep-equals one built after. */
  frozen?: boolean;
}

/** Mirrors `Unit.isRampSet`, CoachProgression.swift:292.
 *  ⚠️ READ OFF THE TYPE THE COACH AUTHORED, NEVER OFF THE WEIGHT. */
export const isRampSet = (s: SetRow): boolean => s.setType === "warm_up";

/** Mirrors `Unit.workingSets`, CoachProgression.swift:285. */
export const workingSets = (u: Unit): SetRow[] => u.sets.filter((s) => !isRampSet(s));
/** Mirrors `Unit.rampSets`, CoachProgression.swift:286. */
export const rampSets = (u: Unit): SetRow[] => u.sets.filter(isRampSet);
/** Mirrors `Unit.isRampOnly`, CoachProgression.swift:290. */
export const isRampOnly = (u: Unit): boolean =>
  u.sets.length > 0 && workingSets(u).length === 0;
/** Mirrors `Unit.ruledSets`, CoachProgression.swift:295. */
export const ruledSets = (u: Unit): SetRow[] => (isRampOnly(u) ? u.sets : workingSets(u));
/** Mirrors `Unit.unruled`, CoachProgression.swift:263. */
export const unruled = (u: Unit): Unit => ({ ...u, rule: null });

// ── Grouping ──────────────────────────────────────────────────────────────────────────────────

/** Mirrors `CoachProgression.unitKey`, CoachProgression.swift:313.
 *
 *  ⚠️ THE KEY IS THE **SET** OF PER-SET DOSES, NOT THE LIST. A split prescription is 3 sets in
 *  one row and 1 in another, so their LISTS differ by length and the two halves of his weighted
 *  pull-up stayed apart — 23 rows produced 23 units, the grouping doing nothing at all.
 *  ⚠️ AND THE SECTION ROLE IS PART OF THE KEY: a rule applies to MAIN work alone, so merging a
 *  warm-up row into a main one would drag it under a rule it must never see.
 *  ⚠️ TWO SPELLINGS OF THIS KEY IS THE FAILURE THIS REPO IS NAMED FOR — the Swift extracted it
 *  so `units` and `CoachNextWeek.bind` could not drift, and this is now a THIRD site. It is
 *  pinned by the fixture and by nothing else. */
export function unitKey(
  libraryExerciseId: string,
  sectionRole: SectionRole,
  sets: SetRow[],
): string {
  const distinct = Array.from(new Set(sets.map(setDoseSignature))).sort();
  return `${libraryExerciseId.toUpperCase()}|${sectionRole}|${distinct.join(",")}`;
}

/** Mirrors `CoachProgression.units`, CoachProgression.swift:318.
 *  Units come back sorted by the ordinal of their FIRST row; the sets inside a unit stay in the
 *  order the day states them. */
export function units(rows: Row[]): Unit[] {
  const order: string[] = [];
  const byKey = new Map<string, Row[]>();
  for (const row of [...rows].sort((a, b) => a.ordinal - b.ordinal)) {
    const key = unitKey(row.libraryExerciseId, row.sectionRole, row.sets);
    if (!byKey.has(key)) {
      order.push(key);
      byKey.set(key, []);
    }
    byKey.get(key)!.push(row);
  }
  const out: Unit[] = [];
  for (const key of order) {
    /* ⚠️ `noUncheckedIndexedAccess` IS ON AND THE INVARIANTS ARE STATED RATHER THAN SILENCED
       WITH `!`. A key only enters `order` at the moment its first row is pushed, so the group is
       never empty — but a `!` says "trust me" to the next reader, and a guard says WHY. */
    const group = byKey.get(key) ?? [];
    const first = group[0];
    if (first === undefined) continue;
    out.push({
      libraryExerciseId: first.libraryExerciseId,
      name: first.name,
      sectionRole: first.sectionRole,
      /* The Swift `Row.init` stores `isHold && !measuredInDistanceOrRounds`; this twin's `Row` is a plain record, so the
         same AND is taken where the unit reads it. */
      isHold: first.isHold && first.measuredInDistanceOrRounds !== true,
      rule: first.rule,
      rowIds: group.map((r) => r.id),
      sets: group.flatMap((r) => r.sets),
      ordinal: first.ordinal,
      ...(group.some((r) => r.measuredInDistanceOrRounds === true) ? { frozen: true } : {}),
    });
  }
  return out;
}

// ── Pairing a day against what was logged ─────────────────────────────────────────────────────

/** Mirrors `CoachProgression.pair`, CoachProgression.swift:364.
 *
 *  ⚠️ PAIRED BY ORDER, NEVER BY THE ORDINAL'S VALUE — the two sides count from different numbers
 *  and a comparison would pair nothing while compiling perfectly.
 *  ⚠️ AND IT IS COMPLETE OR EMPTY, NEVER PARTIAL: every row after a mismatch names the wrong
 *  prescription and looks entirely plausible, so a day whose sides disagree in LENGTH, or in any
 *  library id at any position, is refused whole.
 *  Returns `null` for "do not trust this pairing" — the Swift's `nil`. */
export function pair(day: Row[], logged: LoggedSession): Map<string, LoggedSet[]> | null {
  // F15 4.1b — a HIDDEN row was never shown to the client, so it has no logged row to pair: the day is the rest.
  const plan = day.filter((r) => r.hidden !== true).sort((a, b) => a.ordinal - b.ordinal);
  const performed = [...logged.rows].sort((a, b) => a.position - b.position);
  if (plan.length !== performed.length || plan.length === 0) return null;
  const out = new Map<string, LoggedSet[]>();
  for (let i = 0; i < plan.length; i++) {
    const planned = plan[i];
    const done = performed[i];
    // The lengths were compared above, so neither can be missing; the guard states it rather
    // than asserting it, and it refuses the pairing whole — the same answer as a mismatch.
    if (planned === undefined || done === undefined) return null;
    const namedId = done.libraryExerciseId;
    /* ⚠️ COMPARED CASE-INSENSITIVELY, AND THAT IS NOT TIDINESS. Postgres renders a uuid
       LOWERCASE and Swift's `UUID.uuidString` is UPPERCASE — the two spellings of one id are
       what cost this repo five broken storage paths (`docs/TRAP-CODEX.md`, the `StoragePath`
       entry). A twin fed rows from PostgREST and ids from a Swift-shaped fixture would compare
       the same uuid against itself and refuse every pairing, silently, for every athlete. */
    if (namedId === null || namedId.toUpperCase() !== planned.libraryExerciseId.toUpperCase()) {
      return null;
    }
    out.set(planned.id, done.sets.filter((s) => !isWarmupLog(s)));
  }
  return out;
}

/** Mirrors `CoachProgression.bestLoggedSession`, CoachProgression.swift:209.
 *
 *  ⚠️ A DAY LEGITIMATELY HAS MORE THAN ONE `workout_sessions` ROW AND MOST ARE NOT TRAINING —
 *  browsing writes a shell beside the real work, and one day's training can be split across
 *  several records (40 live pairs). The record carrying the most logged sets is the trained one.
 *  ⚠️ TIES ARE BROKEN BY THE CALLER'S ORDER, WHICH THE CALLER THEREFORE OWES: `<` rather than
 *  `<=` keeps the FIRST of an equal pair, so candidates must arrive oldest first. */
export function bestLoggedSession(candidates: LoggedSession[]): LoggedSession | null {
  let best: LoggedSession | null = null;
  let bestCount = -1;
  for (const c of candidates) {
    const count = c.rows.reduce((n, r) => n + r.sets.length, 0);
    if (bestCount < count) {
      best = c;
      bestCount = count;
    }
  }
  return best;
}

// ── The state a rule carries, DERIVED from the history ────────────────────────────────────────

export type Verdict = "clean" | "missed" | null;

/** Mirrors `CoachProgression.PriorSession`, CoachProgression.swift:383. */
export interface PriorSession {
  date: string;
  doseSignature: string;
  verdict: Verdict;
}

/** Mirrors `CoachProgression.cleanStreak`, CoachProgression.swift:403.
 *
 *  ⚠️ AN UNLOGGED WEEK **PAUSES** THE COUNT, IT DOES NOT END IT. Every rule answers "not logged"
 *  by FREEZING and leaving its counter alone, so a stored counter survives a week nobody
 *  trained. A derivation that stopped at the first gap would read clean/unlogged/clean as a
 *  streak of one where the stored counter reads two, and the athlete would lose a step they had
 *  earned. Skipping is what makes the two definitions the same definition. */
export function cleanStreak(history: PriorSession[], currentDose: string): number {
  let streak = 0;
  for (const session of history) {
    if (session.doseSignature !== currentDose) break;
    if (session.verdict === "clean") streak += 1;
    else if (session.verdict === "missed") return streak;
    // null: continue — see above.
  }
  return streak;
}

/** Mirrors `CoachProgression.consecutiveMisses`, CoachProgression.swift:426.
 *  Same reason in the other direction: a week nobody trained is not a miss, and §5.1's
 *  two-consecutive-misses hold must not be triggered by two holidays. */
export function consecutiveMisses(history: PriorSession[]): number {
  let misses = 0;
  for (const session of history) {
    if (session.verdict === "missed") misses += 1;
    else if (session.verdict === "clean") return misses;
  }
  return misses;
}

/** Mirrors `CoachProgression.doseSignature`, CoachProgression.swift:441. */
export const doseSignature = (sets: SetRow[]): string =>
  sets.map(setDoseSignature).join(",");

/** Mirrors `CoachProgression.workingDoseSignature`, CoachProgression.swift:448.
 *  The dose a RULE reads, so a coach who moves a ramp weight does not reset a counter the ramp
 *  never fed. */
export const workingDoseSignature = (u: Unit): string => doseSignature(ruledSets(u));

/** Mirrors `CoachProgression.uniform`, CoachProgression.swift:469. */
export function uniform(weights: number[]): boolean {
  return new Set(weights.map((w) => fixed(w, 3))).size <= 1;
}

/** Mirrors `CoachProgression.loadTarget`, CoachProgression.swift:457.
 *  The ONE definition of the single load a rule is handed: the weight itself when every working
 *  set carries the same one, the heaviest otherwise. */
export function loadTarget(sets: SetRow[]): number | null {
  const weights = sets.map((s) => s.weightKg).filter((w): w is number => w !== null);
  const first = weights[0];
  if (first === undefined) return null;
  return uniform(weights) ? first : Math.max(...weights);
}

/** Mirrors `CoachProgression.perSetLoads`, CoachProgression.swift:464.
 *  COMPLETE when the working weights differ and every set names one, EMPTY otherwise — never
 *  partial (V7-1's rule: a `compactMap` shortens silently and a four-set day reads as three). */
export function perSetLoads(sets: SetRow[]): number[] {
  const weights = sets.map((s) => s.weightKg).filter((w): w is number => w !== null);
  return !uniform(weights) && weights.length === sets.length ? weights : [];
}

/** Mirrors `CoachProgression.blockName`, CoachProgression.swift:537.
 *  ⚠️ A MAPPING AND NEVER A CAST — three section types against many block names. What matters is
 *  that a warm-up never reads as main. */
export function blockName(role: SectionRole): string {
  switch (role) {
    case "warm_up": return "warm_up";
    case "cool_down": return "cool_down";
    case "recovery": return "mobility";
    case "main": return "main";
  }
}

// ── Redistribution ────────────────────────────────────────────────────────────────────────────

export type Outcome =
  | { kind: "changed"; sets: SetRow[]; reason: string }
  | { kind: "unchanged"; reason: string }
  | { kind: "refused"; reason: string };

/** The fields of `ProgramSessionExercise` that `redistribute` actually reads. A narrow view on
 *  purpose: a full port of that type would be a second statement of a 40-column model, and every
 *  column it did not need would be one more thing to keep in step. */
export interface PlannedPrescription {
  sets: number;
  repsMin: number | null;
  repsMax: number | null;
  perSetReps: number[];
  holdSecondsMin: number | null;
  targetWeightKg: number | null;
  perSetWeightKg: number[];
}

/** Mirrors `CoachProgression.redistribute`, CoachProgression.swift:577.
 *
 *  ⚠️ A SET COUNT CHANGE ON A UNIT SPLIT ACROSS ROWS IS REFUSED, NOT GUESSED. The split exists
 *  because of the owner's own rule for an unequal superset, and deciding which side a gained set
 *  belongs to is a re-split, not arithmetic — `superset_with_next` is a BOUNDARY flag, so getting
 *  it wrong produces a pairing the athlete then actually performs.
 *  ⚠️ THE RAMP SETS PASS THROUGH UNTOUCHED, IN THEIR OWN POSITIONS.
 *  ⚠️ A NON-UNIFORM HOLD IS ONLY REWRITTEN WHEN A RULE ACTUALLY MOVED IT — the unconditional
 *  version was a SILENT REDUCTION (6,6,4 → 4,4,4 with no rule firing), found on his own live
 *  week because every fixture holds uniform seconds. */
export function redistribute(planned: PlannedPrescription, unit: Unit): Outcome {
  const current = unit.sets;
  if (isRampOnly(unit)) return { kind: "unchanged", reason: "" };
  const working = workingSets(unit);
  if (planned.sets !== working.length && unit.rowIds.length > 1) {
    return {
      kind: "refused",
      reason:
        `${unit.name}: the rule asks for ${planned.sets} working sets instead of ` +
        `${working.length}, but this prescription is split across ${unit.rowIds.length} rows to ` +
        `keep a superset even. Re-splitting it is a decision about the pairing, not arithmetic, ` +
        `so the sets are left as they are.`,
    };
  }
  const originalTarget = loadTarget(working);
  const originalPerSet = perSetLoads(working);
  const delta = (planned.targetWeightKg ?? originalTarget ?? 0) - (originalTarget ?? 0);
  const seconds = working.map((s) => s.timeSeconds).filter((v): v is number => v !== null);
  const originalMinSeconds = seconds.length === 0 ? null : Math.min(...seconds);

  function rebuilt(index: number): SetRow {
    // A gained set copies the LAST working set — the shape a rule that adds one is adding more OF.
    const template: SetRow =
      (index < working.length ? working[index] : working[working.length - 1])
      ?? setRow({ position: 0 });
    const out: SetRow = {
      position: index,
      reps: template.reps,
      weightKg: template.weightKg,
      timeSeconds: template.timeSeconds,
      restSeconds: template.restSeconds,
      setType: template.setType,
    };
    if (out.reps !== null) {
      out.reps = planned.perSetReps[index] ?? planned.repsMin ?? out.reps;
    }
    if (out.timeSeconds !== null) {
      if (planned.holdSecondsMin !== null && planned.holdSecondsMin !== originalMinSeconds) {
        out.timeSeconds = planned.holdSecondsMin;
      }
    }
    if (out.weightKg !== null) {
      const weight = out.weightKg;
      const ruleWrote = planned.perSetWeightKg[index];
      const wasThere = originalPerSet[index];
      if (ruleWrote !== undefined && wasThere !== undefined && ruleWrote !== wasThere) {
        // A rule rewrote THIS set's weight itself.
        out.weightKg = ruleWrote;
      } else if (Math.abs(delta) > 1e-9) {
        out.weightKg = snapped(weight + delta) ?? weight + delta;
      } else if (planned.perSetWeightKg.length === 0 && planned.targetWeightKg !== null) {
        out.weightKg = planned.targetWeightKg;
      }
    }
    return out;
  }

  const out: SetRow[] = [];
  let workingIndex = 0;
  for (const set of current) {
    if (isRampSet(set)) {
      out.push(set);
      continue;
    }
    if (workingIndex < planned.sets) out.push(rebuilt(workingIndex));
    workingIndex += 1;
  }
  while (workingIndex < planned.sets) {
    out.push(rebuilt(workingIndex));
    workingIndex += 1;
  }
  const numbered = out.map((s, index) => ({ ...s, position: index }));
  /* ⚠️ COMPARED BY DOSE, NEVER BY THE WHOLE ROW — POSITION IS NOT A VALUE. A unit split across
     two rows carries positions 0,1,2 and then 0 again, because each ROW numbers its own sets from
     zero; the rebuilt list numbers the unit 0,1,2,3. Comparing the structs made every split
     prescription read as CHANGED with an identical before and after. */
  return doseSignature(numbered) === doseSignature(current)
    ? { kind: "unchanged", reason: "" }
    : { kind: "changed", sets: numbered, reason: "" };
}

/** Mirrors the SHAPE half of `CoachProgression.prescription(for:sessionId:state:)`,
 *  CoachProgression.swift:491 — everything the synthetic row says about the DOSE. The config half
 *  (the authored rule merged under the derived state) belongs to the rule engine and is not here.
 *
 *  ⚠️ BUILT FROM `ruledSets`, NOT FROM EVERY SET. Counting the typed ramp sets here while `pair`
 *  drops their logs is the 2026-09-10 finding: a row shaped 2 ramp + 3 working read "only 3 of 5
 *  sets were completed" with every set done exactly as asked, and the second such week opened
 *  §5.1's hold on his Seated Barbell Press and Back Squat, live.
 *  ⚠️ `perSetReps` IS CARRIED ONLY WHERE THE SETS REALLY DIFFER — an array as long as `sets`
 *  whose values are all equal is the same prescription written twice. */
export function plannedFrom(unit: Unit): PlannedPrescription {
  const core = ruledSets(unit);
  const reps = core.map((s) => s.reps).filter((r): r is number => r !== null);
  const seconds = core.map((s) => s.timeSeconds).filter((v): v is number => v !== null);
  const uniformReps = new Set(reps).size <= 1;
  return {
    sets: core.length,
    repsMin: reps.length === 0 ? null : Math.min(...reps),
    repsMax: reps.length === 0 ? null : Math.max(...reps),
    perSetReps: !uniformReps && reps.length === core.length ? reps : [],
    holdSecondsMin: seconds.length === 0 ? null : Math.min(...seconds),
    targetWeightKg: loadTarget(core),
    perSetWeightKg: perSetLoads(core),
  };
}

// ── The seam this file deliberately does not cross ────────────────────────────────────────────

/* 🔴 THE `RuleEngine` INTERFACE THAT STOOD HERE IS DELETED, AND ITS DELETION IS THE POINT.
 *
 * It declared a seam with no implementation so a caller wanting verdicts had to supply one. That
 * was right while the §5 rules were untranscribed. They are transcribed now — `_shared/v6rules.ts`
 * is a complete twin of `V6Progression` — and the join lives in `_shared/coachPlan.ts`, whose
 * `plan()` mirrors `CoachProgression.plan` and calls `v6rules.nextWeek` DIRECTLY, building its
 * input in place.
 *
 * ⚠️ THE INTERFACE WAS NOT MERELY REDUNDANT, IT HAD GONE STALE — AND A STALE CONTRACT IS WORSE
 * THAN NO CONTRACT. It declared `checkIn` where the twin takes `painfulRegions`, a non-null
 * `sectionRole`, a `rule: unknown` on the row, and A RESULT WITH NO REFUSAL CASE and no
 * `questions` / `stops` / `testedMaxUpdates` / `budget`. Anything written to satisfy it would have
 * dropped four outputs and been unable to say "the port refused this week" — and a future reader
 * would have taken it as the shape to build against, because that is what an exported interface
 * is for.
 *
 * WHY THE JOIN IS ITS OWN MODULE rather than a function here: THIS FILE MUST NOT DEPEND ON THE
 * RULES. That independence is what lets `v6rules.ts` know nothing about coach rows and this file
 * know nothing about §5, with everything that must know BOTH vocabularies in one place a reader
 * can hold in their head. `coachPlanPlan.test.ts` tests that join; `progressionTwin.test.ts`
 * asserts this file exports no rule engine and that the interface stays deleted.
 *
 * ⚠️ AND NOTHING LIVE HAS CHANGED: `apply-next-week` still answers 501 and its flag is still off.
 * The code existing and a week being written are different things.
 */

// ── The auto-apply calendar — a COMPLETE transcription ────────────────────────────────────────

/** Mirrors `CoachAutoApplyDue.Assigned`, CoachAutoApplyDue.swift:30.
 *  `scheduledDate` is a `YYYY-MM-DD` day key, not a timestamp: this file is pure and the phone's
 *  column is a bare `date` with no zone at all. */
export interface Assigned {
  rootWorkoutId: string;
  scheduledDate: string | null;
  hiddenFromClient: boolean;
}

/** Mirrors `CoachAutoApplyDue.Due`, CoachAutoApplyDue.swift:37. */
export interface Due {
  rootWorkoutId: string;
  sourceDate: string;
  targetDate: string;
  weekNumber: number;
}

/** The Monday of a day key's week. Mirrors `ProgramWeekCalendar.weekStart`.
 *  ⚠️ MONDAY-FIRST, NEVER THE LOCALE'S `startOfWeek` — the Swift says so explicitly, and a
 *  server running in a locale whose week starts on Sunday would shift every boundary by a day. */
export function weekStart(dayKey: string): string {
  const t = Date.parse(dayKey + "T00:00:00Z");
  const d = new Date(t);
  // getUTCDay: 0 = Sunday. Monday-first means Sunday is 6 days into the week.
  const back = (d.getUTCDay() + 6) % 7;
  return new Date(t - back * 86_400_000).toISOString().slice(0, 10);
}

/** The day `offset` days from a day key. */
export function shiftDay(dayKey: string, offset: number): string {
  return new Date(Date.parse(dayKey + "T00:00:00Z") + offset * 86_400_000)
    .toISOString().slice(0, 10);
}

/** Mirrors `CoachAutoApplyDue.due`, CoachAutoApplyDue.swift:49.
 *
 *  THE RULE AS A SENTENCE: on any day of THIS week, every workout the client was given LAST week
 *  — dated, and not hidden by the coach — that has NOT been given again this week is due, one
 *  week after the day it was given. A workout given twice last week is due twice.
 *  WHY last week → this week and not this week → next: the writer progresses from what was
 *  LOGGED, and a week's logs are complete only once the week is over.
 *  WHY a hidden source is skipped: `hidden_from_client` is the coach's own "I am still looking at
 *  this", and a week the coach has not released is not one the machine may progress from. */
export function due(assignments: Assigned[], nowDayKey: string): Due[] {
  const thisWeek = weekStart(nowDayKey);
  const lastWeek = shiftDay(thisWeek, -7);
  const dated = assignments
    .filter((a): a is Assigned & { scheduledDate: string } => a.scheduledDate !== null)
    .map((a) => ({ root: a.rootWorkoutId, date: a.scheduledDate, hidden: a.hiddenFromClient }));
  const givenThisWeek = new Set(
    dated.filter((a) => weekStart(a.date) === thisWeek).map((a) => a.root),
  );
  const out: Due[] = [];
  for (const a of dated) {
    if (weekStart(a.date) !== lastWeek || a.hidden || givenThisWeek.has(a.root)) continue;
    const earlierWeeks = new Set(
      dated.filter((b) => b.root === a.root && weekStart(b.date) < thisWeek)
        .map((b) => weekStart(b.date)),
    );
    out.push({
      rootWorkoutId: a.root,
      sourceDate: a.date,
      targetDate: shiftDay(a.date, 7),
      weekNumber: earlierWeeks.size + 1,
    });
  }
  /* ⚠️ SORTED BY SOURCE DATE, and the Swift sorts by `Date` while this sorts `YYYY-MM-DD`
     strings. They agree for every value in this vocabulary because an ISO day key sorts
     lexicographically exactly as it sorts chronologically — which is the reason the column is
     that shape, and is worth stating because it is the kind of agreement that stops being true
     the moment somebody "helpfully" accepts another format. */
  return out.sort((a, b) => (a.sourceDate < b.sourceDate ? -1 : a.sourceDate > b.sourceDate ? 1 : 0));
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * TOWARDS `plan()` — the two remaining helpers with no dependencies of their own.
 *
 * `CoachProgression.plan` (CoachProgression.swift:798) is the orchestrator the parity fixture
 * pins. ⚠️ A FIRST COUNT OF WHAT IT STILL NEEDED SAID TEN AND WAS WRONG: the check grepped for
 * `export function` alone, and `ruledSets`, `doseSignature`, `workingDoseSignature` and `unruled`
 * are all `export const` in this file already, while `isHold` is a FIELD on `Unit`. The real list
 * is SEVEN, and these are the two that stand on nothing.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `CoachProgression.doseMoved`, CoachProgression.swift:678.
 *
 *  🔴 WHAT IT IS FOR: `plan` uses it to catch A RULE WHOSE CHANGE COULD NOT BE WRITTEN. The rule
 *  moved the prescription, `redistribute` came back `.unchanged`, and the rule's SENTENCE was
 *  about to reach the coach as an applied change — "+2.5 kg to 2.5 kg" on a row whose sets carry
 *  no weight at all is the live shape that produced it. Without this the coach reads a change
 *  that was never written.
 *
 *  ⚠️ THE LOADS ARE COMPARED AS `%.3f` STRINGS, NOT AS NUMBERS, and that is transcribed rather
 *  than tidied: it is the same equality-by-formatting the Swift uses in `SetRow.doseSignature`
 *  and `uniform`, so two weights that PRINT the same are the same weight here. A `===` on the
 *  doubles would make 2.4999999999999996 a movement, and the twin would report a change the
 *  Swift did not. */
export function doseMoved(before: PlannedPrescription, after: PlannedPrescription): boolean {
  const kg = (v: number | null | undefined): string =>
    (v === null || v === undefined ? "-" : v.toFixed(3));
  const sameNums = (a: number[], b: number[]): boolean =>
    a.length === b.length && a.every((v, i) => v === b[i]);
  const sameKgs = (a: number[], b: number[]): boolean =>
    a.length === b.length && a.every((v, i) => kg(v) === kg(b[i]));
  return before.sets !== after.sets
    || before.repsMin !== after.repsMin || before.repsMax !== after.repsMax
    || !sameNums(before.perSetReps, after.perSetReps)
    || before.holdSecondsMin !== after.holdSecondsMin
    || kg(before.targetWeightKg) !== kg(after.targetWeightKg)
    || !sameKgs(before.perSetWeightKg, after.perSetWeightKg);
}

/** Mirrors `CoachProgression.dateText`, CoachProgression.swift:1082 — `d MMMM`, en_GB, UTC.
 *  It reaches the coach inside two of `plan`'s warnings, so its spelling is part of the verdict
 *  the parity fixture compares and not a display detail.
 *
 *  ⚠️ NO `toLocaleDateString` AND NO TIMEZONE MATH. The Swift pins the locale to en_GB and the
 *  zone to UTC precisely so the string cannot vary by machine; `toLocaleDateString` would hand
 *  that back to whatever ICU the runtime happens to carry, and a Deno edge function and a
 *  developer's laptop are not the same runtime. The month names are a literal table and the
 *  fields are read with `getUTC*` — the same decision this file's header states about refusing to
 *  carry a `Date` across the seam.
 *
 *  ⚠️ AND AN UNPARSEABLE DATE RETURNS THE INPUT UNCHANGED rather than "Invalid Date": the caller
 *  is building a sentence a coach reads, and echoing what it was given is legible where the word
 *  "Invalid" in the middle of a warning is not. */
const MONTHS_EN_GB = ["January", "February", "March", "April", "May", "June",
                      "July", "August", "September", "October", "November", "December"];
export function dateText(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS_EN_GB[d.getUTCMonth()]}`;
}
